(ns nl.mediquest.ui.core.card
  (:require
   [clojure.spec.alpha :as s]
   [integrant.core :as ig]
   [nl.mediquest.ui.component :as component]
   [nl.mediquest.ui.html :refer [card]]
   [nl.mediquest.ui.util :refer [optional-ref matches-key?]]))

(s/def :link/title string?)
(s/def :link/href string?)

(s/def :card/title string?)
(s/def :card/labels (s/coll-of fn?))
(s/def :card/description string?)
(s/def :card/content fn?)
(s/def :card/components (s/coll-of fn?))
(s/def :card/on-click fn?)
(s/def :card/link (s/keys :req [:link/title :link/href]))
(s/def :card/image string?)
(s/def :card/icon keyword?)
(s/def :card/icon-color keyword?)
(s/def :card/icon-weight #{:fa :fal :fas :far :fad})
(s/def :card/readonly? boolean?)
(s/def :card/readonly-reason string?)
(s/def :card/enabled-when-section string?)
(s/def :card/readonly-from-date inst?)
(s/def :card/height (s/or :string string? :keyword keyword?))

(defn set-md [k md]
  (component/put k :md md))

(defn- attributes [{:card/keys [on-click h-full enabled? height]}]
  {:on-click on-click
   :class [(when on-click (if enabled? :cursor-pointer :cursor-not-allowed))
           (when h-full :h-full)
           :mx-2
           height]})

(defn- render-icon
  [{:card/keys [icon icon-color icon-weight enabled?]
    :or {icon-weight :fa
         icon-color :text-primary}}]
  [:div.text-6xl.pr-4.text-center
   {:class [icon
            (if enabled? icon-color :text-gray-600)
            (if enabled? icon-weight :fal)
            "w-1/4"
            "lg:w-1/5"]}])

(defn- render-image [{:card/keys [image enabled?]}]
  [:div.mr-4.m-auto.flex.items-center
   {:class ["w-1/4" "lg:w-1/5" "h-[3.75rem]" (when-not enabled? "grayscale")]}
   [:img.m-auto {:src image}]])

(defn- icon-or-image? [{:card/keys [icon image] :as _opts}]
  (or icon image))

(defn- render-icon-or-image [{:card/keys [icon image] :as opts}]
  (cond
    icon [render-icon opts]
    image [render-image opts]))

(defn- render-body [{:card/keys [title content description enabled?] :as opts}]
  [:div
   {:class [(when (icon-or-image? opts) "w-3/4 lg:w-4/5")]}
   (when title
     [:div {:class [:font-semibold (if enabled? :text-gray-900 :text-gray-600)]} title])
   (when description
     [:div {:class [(if enabled? :text-gray-800 :text-gray-500)]} (apply str description)])
   (when content
     [content])])

(defn- render-content
  [context params
   {:card/keys [labels h-full components link enabled? readonly-reason] :as opts}]
  [:div.flex.flex-col
   {:class [(when h-full :h-full)
            (when h-full :justify-between)]}
   [:div.flex
    [render-icon-or-image opts]
    [render-body opts]]
   (cond
     (seq labels)
     [:div.w-full
      [:div.flex.justify-center
       (doall
        (for [label (sort-by (comp :component/position meta) labels)]
          ^{:key label}
          [label]))]]
     (seq components)
     [:div.w-full
      [:div.flex.flex-col.lg:flex-row.justify-between.overflow-hidden
       (doall
        (for [component (sort-by (comp :component/position meta) components)]
          ^{:key (gensym)}
          [component (merge context {:nested? true}) params]))]])
   (when-let [{:link/keys [title href]} link]
     [:a.text-right.font-semibold.py-2.px-4.print:hidden {:href href} title])
   (when-not enabled? [:span.flex.justify-center.text-red-600.mt-4 readonly-reason])])

(defn later-than-or-equal?
  "True if inst `date` is later than or equal to now. Falsy when not or when `date` is `nil`
  or not `inst?`"
  [date]
  (when (inst? date)
    (nat-int? (compare (js/Date.) date))))

(defn enabled? [readonly? enabled-when-section user-sections readonly-from-date]
  (let [readonly?' (or readonly? (later-than-or-equal? readonly-from-date))]
    (or (not readonly?')
        (contains? user-sections enabled-when-section))))

(defn render [component-key context params]
  (let [{:card/keys [class labels href href-opts on-click readonly? visible?
                     enabled-when-section readonly-from-date]
         :as opts
         :or {class [] visible? true}} @(component/state component-key)
        opts (assoc opts
                    :card/enabled?
                    (enabled? readonly? enabled-when-section (:user/sections opts) readonly-from-date))]
    (when (and (if (fn? visible?)
                 (visible?)
                 visible?)
               (or (nil? labels) (seq labels)))
      [:div.w-full.my-2
       (merge
        {:class (into class [(:md opts)])}
        (when (or href on-click)
          {:role :button}))
       (if (and href (:card/enabled? opts))
         [:a (merge {:href href} href-opts)
          [card (attributes opts) [render-content context params opts]]]
         [card (attributes opts) [render-content context params opts]])])))

(defmethod ig/assert-key :component.ui/card [_k v]
  (assert (s/valid? (s/keys :opt [:card/title
                                  :card/description
                                  :card/content
                                  :card/components
                                  :card/on-click
                                  :card/link
                                  :card/href
                                  :card/href-opts
                                  :card/image
                                  :card/icon
                                  :card/icon-color
                                  :card/icon-weight
                                  :card/readonly?
                                  :card/enabled-when-section
                                  :card/readonly-reason
                                  :card/readonly-from-date
                                  :card/visible?
                                  :card/height])
                    v)))

(defmethod ig/expand-key :component.ui/card [k opts]
  {k (assoc opts :card.component/exclusions
            (optional-ref :card.component/exclusions []))})

(defn- component-init! [k {:card/keys [components]
                           :as opts}]
  (->> {:md
        (if (seq components)
          "md:w-full"
          "md:w-1/2")}
       (merge opts)
       (component/register (last k))))

(defn make-card-component
  [k {:card.component/keys [exclusions]
      :as opts}]
  (component-init! k opts)
  (fn [context params]
    (when-not (matches-key? exclusions k)
      [render (last k) context params])))

(defmethod ig/init-key :component.ui/card
  [k opts]
  (make-card-component k opts))
